import React, { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";

const HERO_SLIDES = [
    {
        src: encodeURI("/Assets/LRS Project 01.jpg"),
        alt: "London Renovation Specialists — completed project.",
    },
];

function prefersReducedMotion() {
    return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export default function HeroCarousel() {
    const isSingle = HERO_SLIDES.length <= 1;
    const reducedMotion = prefersReducedMotion();
    const plugins = (isSingle || reducedMotion)
        ? []
        : [Autoplay({ delay: 7000, stopOnInteraction: true, stopOnMouseEnter: true })];
    const [emblaRef, emblaApi] = useEmblaCarousel(
        { loop: !isSingle, align: "start", watchDrag: !isSingle },
        plugins,
    );
    const [selected, setSelected] = useState(0);

    useEffect(() => {
        if (!emblaApi) return;
        const on = () => setSelected(emblaApi.selectedScrollSnap());
        emblaApi.on("select", on);
        on();
        return () => emblaApi.off("select", on);
    }, [emblaApi]);

    const prev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
    const next = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

    const scrollToForm = () => {
        document.querySelector("#enquiry-form")?.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <>
        <section
            id="hero"
            className="relative w-full min-h-[100dvh] md:min-h-screen overflow-hidden"
            aria-roledescription="carousel"
            aria-label="London Renovation Specialists — project imagery"
            data-testid="hero"
        >
            {/* Embla carousel — fills the viewport on all breakpoints */}
            <div className="embla absolute inset-0" ref={emblaRef}>
                <div className="embla__container flex h-full">
                    {HERO_SLIDES.map((s, i) => (
                        <div key={i} className="embla__slide relative shrink-0 grow-0 basis-full h-full">
                            <img
                                src={s.src}
                                alt={s.alt}
                                className="absolute inset-0 w-full h-full object-cover object-right md:object-center"
                                loading={i === 0 ? "eager" : "lazy"}
                                fetchPriority={i === 0 ? "high" : "auto"}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* Bottom scrim — softens the image where the panel sits */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 via-black/15 to-transparent" />
            {/* Top scrim for nav legibility */}
            <div className="pointer-events-none absolute top-0 left-0 right-0 h-24 md:h-40 bg-gradient-to-b from-black/45 to-transparent" />

            {/* Copy overlay panel — dark translucent card on all breakpoints, bottom-left of hero */}
            <div className="absolute left-6 right-6 md:right-auto bottom-8 md:bottom-20 md:left-12 lg:left-24 md:max-w-lg bg-black/35 backdrop-blur-[2px] rounded-lg p-6 md:p-8 lg:p-10 shadow-2xl z-10 text-center md:text-left">
                <p className="font-manrope font-bold text-offwhite/95 text-base md:text-lg lg:text-xl tracking-wide mb-5 leading-snug" data-testid="hero-kicker">
                    Planning to renovate your London home?
                </p>
                <h1
                    className="font-manrope font-extrabold text-offwhite leading-[0.95] tracking-tight mb-3"
                    style={{ fontSize: "clamp(32px, 6.2vw, 60px)" }}
                    data-testid="hero-headline"
                >
                    Free design service
                </h1>
                <div className="mb-2">
                    <span className="inline-block bg-terracotta/60 rounded-full px-4 py-1.5 font-manrope text-offwhite text-xs md:text-sm leading-snug">
                        From concept to planning-application level drawings
                    </span>
                </div>
                <p className="font-inter text-offwhite/85 text-sm md:text-base tracking-wide leading-relaxed mb-6" data-testid="hero-project-types">
                    Rear extensions · Side extensions · Loft conversions · Internal reconfigurations
                </p>
                <button
                    onClick={scrollToForm}
                    className="font-manrope font-bold text-[13.5px] md:text-[14px] px-6 py-3 rounded-md bg-terracotta text-offwhite hover:bg-terracotta-600 hover:-translate-y-0.5 transition-all shadow-xl"
                    data-testid="hero-cta"
                >
                    Get Your Free Design
                </button>
            </div>

            {/* Desktop arrows — hidden when single slide */}
            {!isSingle && (
                <>
                    <button
                        onClick={prev}
                        aria-label="Previous slide"
                        className="hidden md:flex items-center justify-center absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/15 backdrop-blur border border-white/30 text-white hover:bg-white/25 transition-colors"
                        data-testid="hero-prev"
                    ><ChevronLeft size={22} /></button>
                    <button
                        onClick={next}
                        aria-label="Next slide"
                        className="hidden md:flex items-center justify-center absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/15 backdrop-blur border border-white/30 text-white hover:bg-white/25 transition-colors"
                        data-testid="hero-next"
                    ><ChevronRight size={22} /></button>
                </>
            )}

            {/* Dots — hidden when single slide */}
            {!isSingle && (
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2" role="tablist">
                    {HERO_SLIDES.map((_, i) => (
                        <button
                            key={i}
                            role="tab"
                            aria-label={`Go to slide ${i + 1}`}
                            aria-selected={selected === i}
                            onClick={() => emblaApi && emblaApi.scrollTo(i)}
                            className={
                                "w-1.5 h-1.5 rounded-full transition-all " +
                                (selected === i ? "bg-offwhite w-6" : "bg-offwhite/40 hover:bg-offwhite/70")
                            }
                            data-testid={`hero-dot-${i}`}
                        />
                    ))}
                </div>
            )}

            {/* Scroll indicator — desktop only */}
            <button
                onClick={() => document.querySelector("#what-we-design")?.scrollIntoView({ behavior: "smooth" })}
                aria-label="Scroll to next section"
                className="hidden md:flex items-center justify-center absolute bottom-14 left-1/2 -translate-x-1/2 z-20 text-offwhite/70 hover:text-offwhite animate-chev-bounce"
                data-testid="hero-scroll-indicator"
            ><ChevronDown size={22} /></button>

            {/* Sentinel for sticky mobile CTA — fires when the hero has scrolled off-screen */}
            <div id="hero-end-sentinel" aria-hidden="true" className="absolute bottom-0 left-0 w-px h-px" />
        </section>
        </>
    );
}
